import {
  createWebCryptoSessionPopSigner,
  type CmmdSessionPopSigner,
} from "@cmmd-forge/shared/cmmdSessionPop";

/**
 * Browser-side holder of the CMMD session proof-of-possession key
 * (forge#3517).
 *
 * The browser generates an Ed25519 key pair, stores it in IndexedDB, and hands
 * out a signer. The private half is generated non-extractable, so the page
 * (and anything injected into it) can sign proofs but can never read the key
 * material, the same trust shape the mobile client gets from secure storage.
 * A cookie wipe or IndexedDB clear simply means the next login binds a new
 * key; CMMD treats it as a new binding, not a failure. A browser that cannot
 * hold the key at all fails login closed instead of silently going unbound.
 *
 * Gated by VITE_CMMD_SESSION_POP_ENABLED: while the rollout is in the expand
 * phase the gate stays off, login requests are byte-identical to today, and
 * every client keeps the legacy bearer path.
 */

const DATABASE_NAME = "cmmd-session-pop";
const OBJECT_STORE_NAME = "keys";
const KEY_ID = "device-key";

export function isCmmdSessionPopEnabled(): boolean {
  return import.meta.env.VITE_CMMD_SESSION_POP_ENABLED === "true";
}

interface PersistedSessionPopKeyPair {
  readonly privateKey: CryptoKey;
  readonly publicKeySpkiDer: Uint8Array;
}

/**
 * Thrown when the rollout gate is on but this browser cannot hold its bound
 * session key: no IndexedDB, storage blocked or unreadable, or no WebCrypto
 * Ed25519. Login surfaces the message instead of silently logging in unbound.
 */
export class CmmdSessionPopKeyStorageError extends Error {
  constructor(reason: string) {
    super(
      `CMMD session key storage is unavailable (${reason}). Session binding is enabled, so login cannot continue without a bound key: allow site data and IndexedDB for this origin, then retry.`,
    );
    this.name = "CmmdSessionPopKeyStorageError";
  }
}

export interface LoadCmmdSessionPopSignerOptions {
  /** Injectable for tests; defaults to the browser's IndexedDB. */
  readonly indexedDB?: IDBFactory | null;
  /** Injectable for tests; defaults to the ambient WebCrypto subtle. */
  readonly subtle?: SubtleCrypto;
}

function resolveIndexedDb(factory: IDBFactory | null | undefined): IDBFactory | null {
  if (factory !== undefined) return factory;
  return typeof indexedDB === "undefined" ? null : indexedDB;
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.addEventListener("success", () => resolve(request.result), { once: true });
    request.addEventListener("error", () => reject(request.error), { once: true });
  });
}

/**
 * A request's success event is not durability: only the transaction's
 * complete event proves it committed, and a commit can still abort (quota at
 * commit, for example) and roll every change back. Rejecting on abort and
 * error is what keeps a rolled back key from ever surfacing as a signer.
 */
function transactionToPromise(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.addEventListener("complete", () => resolve(), { once: true });
    transaction.addEventListener(
      "abort",
      () => reject(transaction.error ?? new DOMException("The transaction aborted.", "AbortError")),
      { once: true },
    );
    transaction.addEventListener(
      "error",
      () =>
        reject(transaction.error ?? new DOMException("The transaction failed.", "UnknownError")),
      { once: true },
    );
  });
}

function openDatabase(factory: IDBFactory): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = factory.open(DATABASE_NAME, 1);
    request.addEventListener("upgradeneeded", () => {
      if (!request.result.objectStoreNames.contains(OBJECT_STORE_NAME)) {
        request.result.createObjectStore(OBJECT_STORE_NAME);
      }
    });
    request.addEventListener("success", () => resolve(request.result), { once: true });
    request.addEventListener("error", () => reject(request.error), { once: true });
  });
}

function isPersistedKeyPair(value: unknown): value is PersistedSessionPopKeyPair {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<PersistedSessionPopKeyPair>;
  return (
    candidate.privateKey instanceof CryptoKey && candidate.publicKeySpkiDer instanceof Uint8Array
  );
}

async function readPersistedKeyPair(
  database: IDBDatabase,
): Promise<PersistedSessionPopKeyPair | null> {
  const transaction = database.transaction(OBJECT_STORE_NAME, "readonly");
  const request = transaction.objectStore(OBJECT_STORE_NAME).get(KEY_ID);
  const stored = await requestToPromise(request);
  if (!isPersistedKeyPair(stored)) return null;
  return { privateKey: stored.privateKey, publicKeySpkiDer: stored.publicKeySpkiDer };
}

/**
 * Store the candidate key only while the store still has none, and return the
 * key that actually ended up persisted. The existence check and the write
 * share one readwrite transaction: IndexedDB serializes readwrite
 * transactions against a store, so a concurrent creator either lands
 * entirely first (its stored key is read and returned here, untouched) or
 * entirely second (the duplicate `add` is refused). `add`, not `put`, is what
 * keeps the first persisted key from ever being overwritten. A newly added
 * candidate is returned only once the transaction's complete event fires: a
 * commit that aborts rejects here, so the caller never binds a login to a key
 * IndexedDB rolled back.
 */
async function addKeyPairIfAbsent(
  database: IDBDatabase,
  candidate: PersistedSessionPopKeyPair,
): Promise<PersistedSessionPopKeyPair> {
  const transaction = database.transaction(OBJECT_STORE_NAME, "readwrite");
  const store = transaction.objectStore(OBJECT_STORE_NAME);
  const committed = transactionToPromise(transaction);
  // A failed request aborts this transaction after this function has already
  // thrown; absorb that late rejection so it cannot surface unhandled.
  committed.catch(() => undefined);
  const existing = await requestToPromise(store.get(KEY_ID));
  if (isPersistedKeyPair(existing)) {
    return { privateKey: existing.privateKey, publicKeySpkiDer: existing.publicKeySpkiDer };
  }
  await requestToPromise(store.add(candidate, KEY_ID));
  await committed;
  return candidate;
}

/**
 * In-flight load for this tab. Concurrent callers share one result so two
 * simultaneous logins cannot both mint a candidate; the memo clears once the
 * load settles so a later call re-reads the persisted key.
 */
let pendingLoad: Promise<CmmdSessionPopSigner> | null = null;

/**
 * Return the signer for this browser's bound CMMD session key, creating and
 * persisting the key on first use. Resolves null only while the rollout gate
 * is off; with the gate on, a browser that cannot hold the key rejects with
 * CmmdSessionPopKeyStorageError so login fails closed instead of silently
 * sending an unbound request. Key generation happens outside any IndexedDB
 * transaction (a transaction expires while the event loop waits on
 * WebCrypto), and persistence is one readwrite transaction, so concurrent
 * first-time creations can never overwrite each other's key.
 */
export async function loadOrCreateCmmdSessionPopSigner(
  options: LoadCmmdSessionPopSignerOptions = {},
): Promise<CmmdSessionPopSigner | null> {
  if (!isCmmdSessionPopEnabled()) return null;

  pendingLoad ??= loadGateOnSessionPopSigner(options).finally(() => {
    pendingLoad = null;
  });
  return pendingLoad;
}

async function loadGateOnSessionPopSigner(
  options: LoadCmmdSessionPopSignerOptions,
): Promise<CmmdSessionPopSigner> {
  const factory = resolveIndexedDb(options.indexedDB);
  const subtle = options.subtle ?? crypto.subtle;
  if (!factory) {
    throw new CmmdSessionPopKeyStorageError("IndexedDB is not available in this context");
  }

  let database: IDBDatabase;
  try {
    database = await openDatabase(factory);
  } catch {
    throw new CmmdSessionPopKeyStorageError("the key database could not be opened");
  }

  try {
    const existing = await readPersistedKeyPair(database);
    if (existing) {
      return createWebCryptoSessionPopSigner({
        privateKey: existing.privateKey,
        publicKeySpkiDer: existing.publicKeySpkiDer,
      });
    }

    // Generated outside the transaction below: an IndexedDB transaction
    // auto-commits while the event loop waits on WebCrypto, and generation is
    // the slow step. The transaction re-checks the store, so a key another
    // context persisted meanwhile wins and this candidate is discarded.
    const generated = await subtle.generateKey({ name: "Ed25519" }, false, ["sign"]);
    const pair = generated as CryptoKeyPair;
    const candidate: PersistedSessionPopKeyPair = {
      privateKey: pair.privateKey,
      publicKeySpkiDer: new Uint8Array(await subtle.exportKey("spki", pair.publicKey)),
    };
    return createWebCryptoSessionPopSigner(await addKeyPairIfAbsent(database, candidate));
  } catch (error) {
    if (error instanceof CmmdSessionPopKeyStorageError) throw error;
    // Unsupported algorithm, quota refusal, or an unreadable store: this
    // browser cannot hold a bound key, so refuse to log in unbound.
    throw new CmmdSessionPopKeyStorageError("the session key could not be generated or persisted");
  }
}

/** Resolver wired into the login lanes; overridable in tests. */
export function resolveSessionPopPublicKey(
  options: LoadCmmdSessionPopSignerOptions = {},
): Promise<string | null> {
  return loadOrCreateCmmdSessionPopSigner(options).then((signer) => signer?.publicKeyPem ?? null);
}
